import React, { FunctionComponent, HTMLAttributes, useState } from 'react';

import { useCustomRole, type CustomRoleWithUsage } from '@api/v4/organizations/custom_permissions';
import { I18nProviderWrapper } from '@components/common/I18nProviderWrapper';
import UsageInfoCard from '@components/limits/usage_info_card/UsageInfoCard';

import { AddCustomRoleCard } from './AddCustomRoleCard';
import { CreateCustomRolePage } from './CreateCustomRolePage';
import { CustomRoleManagerWrapper } from './CustomRoleManager';

interface CustomRoleUsageCardProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  organizationKey?: string;
  viewOnly?: boolean;
  hasSetLimits?: boolean;
}

const getPermissionSummary = (role: CustomRoleWithUsage): string => {
  const permissions = [];
  if (role.asset_approve) permissions.push('approve assets');
  if (role.add_to_shares) permissions.push('manage shares');
  if (role.view_asset_usage) permissions.push('view asset usage');
  if (role.view_collection_usage) permissions.push('view collection usage');

  return permissions.length > 0 ? permissions.join(', ') : 'basic';
};

export const CustomRoleUsageCard: FunctionComponent<CustomRoleUsageCardProps> = ({
  organizationKey = undefined,
  viewOnly = false,
  className = '',
  hasSetLimits = false,
  ...props
}) => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const { customRole, loading, error, refreshCustomRole } = useCustomRole(organizationKey);

  const handleCreateRole = (): void => {
    setShowCreateForm(true);
  };

  const handleBackFromCreate = (): void => {
    setShowCreateForm(false);
    void refreshCustomRole(); // Refresh the role data
  };

  const handleDelete = async (): Promise<void> => {
    await refreshCustomRole(); // Refresh after delete to update UI state
    document.getElementById('custom-role-usage-card')?.classList.remove('has-custom-role');
  };

  if (loading) {
    return (
      <I18nProviderWrapper>
        <div className="alert well-sm level-limits-card">
          <div>Loading custom role...</div>
        </div>
      </I18nProviderWrapper>
    );
  }

  if (error) {
    return (
      <I18nProviderWrapper>
        <div className="alert well-sm level-limits-card alert-danger">
          <div>Error: {error}</div>
        </div>
      </I18nProviderWrapper>
    );
  }

  // Show create form if user clicked to create/edit
  if (showCreateForm) {
    return (
      <I18nProviderWrapper>
        <CustomRoleManagerWrapper>
          <CreateCustomRolePage
            existingRole={customRole}
            onBack={handleBackFromCreate}
            onDelete={handleDelete}
            organizationKey={BFG.organization_key}
          />
        </CustomRoleManagerWrapper>
      </I18nProviderWrapper>
    );
  }

  // Show "Add Custom Role" card if no role exists
  if (!customRole && !viewOnly) {
    return (
      <I18nProviderWrapper>
        <div className="alert well-sm level-limits-card j-custom-role-card" style={{ ...props.style, order: 4, margin: 0 }}>
          <AddCustomRoleCard onNavigateToCreate={handleCreateRole} />
        </div>
      </I18nProviderWrapper>
    );
  }

  // Show usage card if custom role exists
  if (customRole) {
    const usage = {
      current: customRole.userCount || 0,
      limit: '∞',
    };

    const permissionSummary = getPermissionSummary(customRole);

    return (
      <I18nProviderWrapper>
        <div
          className={className ? className : 'alert well-sm level-limits-card j-user-limits'}
          {...props}
          style={{ ...props.style, order: 2 }}
        >
          <div id={`custom-role-card-${customRole.key}`}>
            <UsageInfoCard
              customClass="level-limits-card"
              editEnabled={true}
              hasSetLimits={hasSetLimits}
              icon="Person"
              isEnabled={true}
              openEditDialog={handleCreateRole}
              orgKey={organizationKey}
              setLimitsAction={!viewOnly ? handleCreateRole : undefined}
              subtitle={'0 pending invitations'}
              title={customRole.label}
              usage={usage}
            />
          </div>
        </div>
      </I18nProviderWrapper>
    );
  }

  // If view_only and no role exists, show nothing
  return null;
};
